/* global Forms, flatpickr */

import ApplicationController from '../application_controller'
import moment from 'moment'

const FLATPICKR_FORMAT = 'Y-m-d'

class EventFormController extends ApplicationController {
  static targets = ['eventType', 'rangeDatepicker', 'startDate', 'endDate', 'halfDay', 'halfDayContainer',
    'workDaysHint', 'requestDaysProgressBar', 'requestedDaysHint', 'availableDaysProgressBar',
    'stats', 'selectedDatesContainer', 'extraDaysWarning', 'extraDaysCheckbox', 'submit', 'holidayInAdvanceCheckbox',
    'holidayInAdvanceInput', 'attachment', 'fileInput', 'destroyAttachment']

  static values = { eventsDates: String, eventMainVacation: Boolean }

  connect () {
    Forms.initCheckbox(this.element)

    flatpickr.l10ns[I18n.locale].firstDayOfWeek = 1
    flatpickr.localize(flatpickr.l10ns[I18n.locale])
    const parsedEventDates = this.eventMainVacationValue ? JSON.parse(this.eventsDatesValue) : []

    this.calendarSettings = {
      enabledDates: [],
      disabledDates: parsedEventDates.map(event => ({ from: event.start_date, to: event.end_date })),
      maxAllowedInAdvanceDays: 0,
      allowInAdvanceVacations: false
    }
    this.progressBarAvailable = document.querySelector('#progress-bar-available')
    this.progressBarPending = document.querySelector('#progress-bar-pending')

    const startDate = this.hasStartDateTarget ? this.startDateTarget.value : null
    const endDate = this.hasEndDateTarget ? this.endDateTarget.value : null
    const options = {
      mode: 'range',
      defaultDate: [startDate, endDate],
      dateFormat: FLATPICKR_FORMAT,
      inline: true
    }

    const initCalendar = () => {
      if (this.calendarSettings.enabledDates.length > 0) {
        const disabledDays = (dObj, dStr, fp, dayElem) => {
          const betweenOrSameDay = (date, eventDates) => {
            return moment(date).isBetween(eventDates.from, eventDates.to) || moment(date).isSame(eventDates.from) || moment(date).isSame(eventDates.to)
          }

          this.calendarSettings.disabledDates.forEach(dates => {
            if (betweenOrSameDay(dayElem.dateObj, dates)) {
              dayElem.className += ' flatpickr-disabled nextMonthDayflatpickr-disabled'
            }
          })
        }
        options.onDayCreate = [disabledDays]
        options.enable = this.calendarSettings.enabledDates
      }

      if (this.hasRangeDatepickerTarget) {
        flatpickr(this.rangeDatepickerTarget, {
          ...options,
          onChange: (_dates, value) => {
            const dates = value.split(flatpickr.l10ns[I18n.locale].rangeSeparator)
            this.startDateTarget.value = dates[0]
            this.endDateTarget.value = dates[1] || dates[0]
            this.handlePeriodUpdate()
          }
        })
      }
    }

    this.getCalendarSettings(initCalendar)

    if (this.hasHalfDayTarget) { this.halfDayTarget.addEventListener('change', this.handlePeriodUpdate.bind(this)) }

    if (startDate && endDate) this.handlePeriodUpdate()
  }

  getCalendarSettings (initCalendar) {
    fetch('/calendar/settings.json')
      .then(response => {
        return response.json()
      }).then(data => {
        if (data) {
          this.calendarSettings.enabledDates = data.enabled_dates
          this.calendarSettings.maxAllowedInAdvanceDays = data.max_allowed_in_advance_days
          this.calendarSettings.allowInAdvanceVacations = data.allow_in_advance_vacations
        }
        initCalendar()
      }).catch(() => {
        initCalendar()
      })
  }

  handlePeriodUpdate () {
    const format = 'DD.MM.YYYY'
    const startDate = this.startDateTarget.value
    const endDate = this.endDateTarget.value
    const halfDay = this.halfDayTarget.checked

    if (startDate && endDate) {
      const selectedDates = _.uniq([moment(startDate).format(format), moment(endDate).format(format)])
      const datesStr = I18n.t('calendar.holiday_entitlements.selected_dates', {
        dates: selectedDates.join(' — '), count: selectedDates.length
      })

      $(this.selectedDatesContainerTarget).html(datesStr)
      $(this.halfDayContainerTarget).toggleClass('hidden', selectedDates.length > 1) // show halfDay checkbox if single day selected
    }

    const requestParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      employee_id: this.rangeDatepickerTarget.dataset.employeeId,
      half_day: halfDay
    })

    fetch(`/calendar/holiday_request_summary.json?${requestParams.toString()}`)
      .then(response => response.json())
      .then(data => {
        if (halfDay && data.working_days === 1) data.working_days = 0.5

        this.updateHints(data.requested_days, data.working_days)

        // Progress
        if (!this.progressBarAvailable) { return }

        this.toggleExtraDaysWarning(data.need_extra_days)
        this.toggleHolidayInAdvance(data.allow_in_advance)

        $(this.progressBarAvailable).css({ flex: data.new_available_days.toString() })
        $(this.progressBarPending).css({ flex: data.pending.toString() })
      })
  }

  toggleExtraDaysWarning (needExtraDays) {
    if (needExtraDays) {
      $(this.extraDaysWarningTarget).slideDown()
      $(this.submitTarget).attr('disabled', true)
    } else {
      $(this.extraDaysWarningTarget).slideUp()
      $(this.submitTarget).attr('disabled', false)
    }
  }

  toggleHolidayInAdvance (allowInAdvance) {
    $(this.holidayInAdvanceCheckboxTarget).prop('checked', false)
    if (allowInAdvance) {
      $(this.holidayInAdvanceCheckboxTarget.parentNode).removeClass('d-none')
    } else {
      $(this.holidayInAdvanceCheckboxTarget.parentNode).addClass('d-none')
      return
    }

    $(this.holidayInAdvanceCheckboxTarget).on('change', (e) => {
      $(this.submitTarget).attr('disabled', !e.target.checked)
      $(this.holidayInAdvanceInputTarget).attr('disabled', !e.target.checked)
      $(this.holidayInAdvanceInputTarget).val(e.target.checked)
    })
  }

  updateHints (requestedDays, workingDays) {
    const hint = (key, value) => I18n.t(`calendar.holiday_entitlements.${key}_hint`, { [key]: value })
    const requestedDaysHintText = hint('requested_days', requestedDays)
    const workDaysHintText = hint('working_days', workingDays)

    $(this.workDaysHintTargets).text(workDaysHintText)
    $(this.requestedDaysHintTargets).text(requestedDaysHintText)
  }

  removeAttachment (event) {
    event.preventDefault()

    if (window.confirm(I18n.t('really_delete'))) {
      this.destroyAttachmentTarget.value = 'true'
      this.attachmentTarget.classList.add('d-none')
      this.fileInputTarget.closest('.form-group').classList.remove('d-none')
    }
  }
}

export default EventFormController
